/**
 * Routing and the one access gate.
 *
 * There is no operator surface here any more: everything an operator does lives
 * in the ValueGameNode admin panel, so this app has one kind of user and one
 * gate. Every page below the shell is still lazy — the console pulls in noVNC,
 * which has no business in the bundle a customer downloads to look at one
 * server.
 */
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, type LoginRedirectState } from './auth/AuthContext';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import { Button, EmptyState, FullPageLoader } from './components/ui';

const Instances = lazy(() => import('./pages/Instances'));
const Activate = lazy(() => import('./pages/Activate'));
const InstanceDetail = lazy(() => import('./pages/InstanceDetail'));
const Access = lazy(() => import('./pages/Access'));
const Firewall = lazy(() => import('./pages/Firewall'));
const Settings = lazy(() => import('./pages/Settings'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const Suspended = lazy(() => import('./pages/Suspended'));
const Console = lazy(() => import('./pages/Console'));

/**
 * Signed-in gate. While the session probe is in flight we must render neither
 * the page nor a redirect: redirecting on `loading` would bounce every
 * hard refresh through /login and lose the destination.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader label="Checking your session" />;
  if (!user) {
    const state: LoginRedirectState = { from: location.pathname + location.search };
    return <Navigate to="/login" replace state={state} />;
  }
  return <>{children}</>;
}

function NotFound() {
  return (
    <EmptyState
      title="Page not found"
      description="That page does not exist, or you do not have access to it."
      action={
        <Button variant="primary" onClick={() => window.location.assign('/')}>
          Back to your servers
        </Button>
      }
    />
  );
}

function Shell() {
  return (
    <RequireAuth>
      <AppShell>
        <Suspense fallback={<FullPageLoader label="Loading" />}>
          <Routes>
            <Route path="/" element={<Instances />} />
            {/* Two ways into the same page: straight at one purchase, or "set
                up whatever I have waiting" — which is also where an old /new
                bookmark from the days of ordering lands. */}
            <Route path="/activate/:id" element={<Activate />} />
            <Route path="/new" element={<Activate />} />
            <Route path="/servers/:id" element={<InstanceDetail />} />
            <Route path="/servers/:id/access" element={<Access />} />
            <Route path="/servers/:id/firewall" element={<Firewall />} />
            <Route path="/servers/:id/upgrade" element={<Upgrade />} />
            <Route path="/servers/:id/settings" element={<Settings />} />
            <Route path="/servers/:id/suspended" element={<Suspended />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AppShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* The console is full-bleed: a sidebar around a VNC canvas wastes
              the only screen where every pixel is the product. */}
          <Route
            path="/servers/:id/console"
            element={
              <RequireAuth>
                <Suspense fallback={<FullPageLoader label="Opening console" />}>
                  <Console />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Shell />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
